import React, { useEffect, useState } from 'react';
import { AlertCircle, Eye, EyeOff, Mail, Lock, User, Sparkles } from 'lucide-react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { getEmailValidationError } from '../../utils/emailValidation';
import {
  parseInviteResolveError,
  isRegisterUserAlreadyExists,
  loginPathWithEmail,
  consumedInviteLoginPath,
  inviteTerminalHeading,
  shouldHideSignupForm,
  shouldRedirectConsumedInviteToLogin,
  shouldShowInviteSignIn,
  type InviteTerminalState,
} from '../../utils/inviteResolveError';
import Toast from '../client/Toast';

// ----- Invitation prepopulate -----
// When the page is reached via a magic-link email
// (e.g. /signup?invite=AbCdEfGh1234567890abcd), we GET the prefill
// payload from the backend proxy and auto-fill the form. The token is
// resolved exactly once on mount; further field changes are owned by
// the user. See BestEmailService/docs/INVITATION_PREPOPULATE_DESIGN.md.
interface InvitePrefill {
  email?: string;
  primary_email?: string;
  alternate_email?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  community?: string;
  unit?: string;
  move_in_date?: string;
  [key: string]: unknown;
}

function invitePrimaryEmail(prefill: InvitePrefill): string {
  const primary =
    (typeof prefill.primary_email === 'string' && prefill.primary_email) ||
    prefill.email ||
    '';
  return primary.trim();
}

/** Deliverable address after admin email correction; empty when unset or same as primary. */
function inviteAlternateEmail(prefill: InvitePrefill): string {
  const alternate =
    typeof prefill.alternate_email === 'string' ? prefill.alternate_email.trim() : '';
  if (!alternate) return '';
  if (alternate.toLowerCase() === invitePrimaryEmail(prefill).toLowerCase()) return '';
  return alternate;
}

interface InviteState {
  invite: string;
  prefill: InvitePrefill;
}

const SignupPage: React.FC = () => {
  const [formData, setFormData] = useState<{
    displayName: string;
    email: string;
    password: string;
    confirmPassword: string;
  }>({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [toast, setToast] = useState<{ isVisible: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({ isVisible: false, message: '', type: 'success' });
  const [searchParams] = useSearchParams();
  const [invite, setInvite] = useState<InviteState | null>(null);
  const [inviteResolving, setInviteResolving] = useState<boolean>(false);
  const [inviteTerminal, setInviteTerminal] = useState<InviteTerminalState | null>(null);
  const navigate = useNavigate();

  const goToLogin = (email?: string, replace = false) => {
    if (email?.trim()) {
      navigate(loginPathWithEmail(email.trim(), 'invite-consumed'), { replace });
    } else {
      navigate('/login?hint=invite-consumed', { replace });
    }
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = () => {
    setToast({ isVisible: false, message: '', type: 'success' });
  };

  const validateForm = () => {
    if (!formData.displayName.trim()) {
      setError('Please enter your name');
      return false;
    }

    // When invited, the email comes straight from the resolved
    // invitation (server-side EmailStr-validated already) — no form
    // input means no client-side check needed.
    if (!invite) {
      const emailError = getEmailValidationError(formData.email);
      if (emailError) {
        setError(emailError);
        return false;
      }
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const getApiBase = () => {
    return (typeof window !== 'undefined' && window.location.hostname === 'localhost')
      ? 'http://localhost:8000/api/'
      : (import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/');
  };

  // Resolve ?invite=… on mount and prefill the form. Pure additive:
  // when there is no invite in the URL, behavior is identical to the
  // pre-prepopulate signup page.
  useEffect(() => {
    const inviteId = searchParams.get('invite');
    if (!inviteId) return;

    let cancelled = false;
    const resolveInvite = async () => {
      setInviteResolving(true);
      try {
        const baseUrl = getApiBase();
        const resp = await fetch(
          `${baseUrl}invitation/resolve?invite=${encodeURIComponent(inviteId)}`,
        );
        if (!resp.ok) {
          let detail: unknown = 'This invitation is no longer valid.';
          try {
            const data = await resp.json();
            if (data?.detail !== undefined) {
              detail = data.detail;
            }
          } catch {
            // ignore JSON parse errors; use default detail
          }
          if (!cancelled) {
            const terminal = parseInviteResolveError(detail);
            if (shouldRedirectConsumedInviteToLogin(terminal)) {
              navigate(consumedInviteLoginPath(terminal), { replace: true });
              return;
            }
            setInviteTerminal(terminal);
          }
          return;
        }
        const data = await resp.json();
        const prefill: InvitePrefill = data?.prefill || {};
        if (cancelled) return;

        setInvite({ invite: inviteId, prefill });
        // Prefer first_name/last_name; fall back to "name" verbatim.
        const composedName =
          [prefill.first_name, prefill.last_name].filter(Boolean).join(' ').trim() ||
          (prefill.name || '');
        setFormData((prev) => ({
          ...prev,
          displayName: composedName || prev.displayName,
          email: prefill.email || prev.email,
        }));
      } catch (e) {
        console.warn('Could not resolve invite token:', e);
        if (!cancelled) {
          setInviteTerminal(parseInviteResolveError(null));
        }
      } finally {
        if (!cancelled) setInviteResolving(false);
      }
    };

    resolveInvite();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  // Best-effort consume of the invite once we know the new user_id.
  // Failures here MUST NOT block the signup happy-path — the user has
  // already created their account. We pass `email` so admin can do a
  // defense-in-depth check that the signed-up address matches the
  // invitation's resident_email; a 409 here only matters for telemetry.
  const consumeInviteAfterSignup = async (userId: number, email: string) => {
    if (!invite) return;
    try {
      const baseUrl = getApiBase();
      await fetch(`${baseUrl}invitation/consume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invite: invite.invite, user_id: userId, email }),
      });
    } catch (e) {
      // Swallow: consume is best-effort.
      console.warn('Failed to mark invitation consumed (non-fatal):', e);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (inviteTerminal) {
      setError('Registration is not available for this invitation link.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const baseUrl = getApiBase();

      // When arriving via an invitation, ALWAYS use the invitation's
      // recipient email — never `formData.email`, which Chrome's
      // autofill happily overwrites even on read-only inputs (and
      // a malicious user could DOM-tweak the field too). This is
      // layer 2 of the email-binding defense (layer 1 is the
      // read-only attribute, layer 3 is admin's /consume mismatch
      // check).
      const submitEmail = invite?.prefill.email || formData.email;

      // Register user with backend
      const response = await fetch(`${baseUrl}auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: submitEmail,
          password: formData.password,
          name: formData.displayName
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (isRegisterUserAlreadyExists(data.detail)) {
          showToast(
            'An account with this email already exists. Taking you to sign in…',
            'info',
          );
          navigate(loginPathWithEmail(submitEmail, 'invite-consumed'), { replace: true });
          return;
        }
        // Handle backend error response
        if (data.detail) {
          if (typeof data.detail === 'string') {
            setError(data.detail);
          } else if (Array.isArray(data.detail)) {
            // FastAPI validation errors
            setError(data.detail.map((err: any) => err.msg).join(', '));
          } else {
            setError('Registration failed. Please try again.');
          }
        } else {
          setError('Registration failed. Please try again.');
        }
        return;
      }

      console.log('Registration successful:', data);

      // If we arrived from a magic-link invitation, mark it consumed
      // so subsequent clicks return 410 Gone. Best-effort; never blocks.
      // Use the same trusted email we sent to /auth/register so admin's
      // mismatch check can't flag a perfectly legitimate signup.
      const newUserId =
        typeof data?.id === 'number' ? data.id : Number(data?.id);
      if (Number.isFinite(newUserId) && newUserId > 0) {
        // Fire-and-forget; do not await before showing the success UI.
        void consumeInviteAfterSignup(newUserId, submitEmail);
      }

      // Show success message
      showToast(
        `Account created successfully! A verification email has been sent to ${formData.email}. Please check your inbox and verify your email.`,
        'success'
      );

      // Redirect to login page after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (error: any) {
      console.error('Signup error:', error);
      setError(`Registration failed: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error when user types
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-3xl shadow-xl p-12 md:p-16">
        <div className="flex flex-col items-center mb-8">
          <div className="mb-6 cursor-pointer">
            <img 
              src="/landing/getpawtected.svg" 
              alt="GetPawtected" 
              className="w-64 md:w-80 lg:w-96 h-auto"
            />
          </div>
          <h1 className="text-4xl md:text-3xl font-medium font-poppins text-black mb-4 text-center">
            {inviteTerminal && !invite
              ? inviteTerminalHeading(inviteTerminal)
              : 'Create Your Account'}
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 text-center font-inter leading-relaxed max-w-md">
            {inviteTerminal && !invite
              ? 'You cannot register with this invitation link.'
              : "Get started with your pet's protection"}
          </p>
        </div>

        {/* Invitation banners (only shown when ?invite=… is in the URL) */}
        {inviteResolving && (
          <div className="mb-6 p-5 bg-blue-50 border border-blue-200 rounded-2xl flex items-center gap-4">
            <div className="w-5 h-5 border-2 border-[color:var(--pmc-accent)] border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
            <p className="text-base text-blue-800 font-inter">Loading your invitation…</p>
          </div>
        )}

        {invite && !inviteResolving && (
          <div className="mb-6 p-5 bg-green-50 border border-green-200 rounded-2xl flex items-start gap-4">
            <Sparkles className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-base text-green-800 font-inter leading-relaxed">
              {invite.prefill.community
                ? <>We&apos;ve prepared this for <strong>{invite.prefill.community}</strong>{invite.prefill.unit ? <>, unit <strong>{invite.prefill.unit}</strong></> : null}. Just set a password to finish.</>
                : <>We&apos;ve prepared your invitation. Just set a password to finish.</>}
            </p>
          </div>
        )}

        {inviteTerminal && !invite && !inviteResolving && (() => {
          const isConsumedOrphan =
            inviteTerminal.kind === 'consumed' && inviteTerminal.accountExists === false;
          const bannerTone = isConsumedOrphan
            ? 'bg-red-50 border border-red-200'
            : inviteTerminal.kind === 'consumed'
              ? 'bg-blue-50 border border-blue-200'
              : 'bg-amber-50 border border-amber-200';
          const iconTone = isConsumedOrphan
            ? 'text-red-600'
            : inviteTerminal.kind === 'consumed'
              ? 'text-blue-600'
              : 'text-amber-600';
          const textTone = isConsumedOrphan
            ? 'text-red-800'
            : inviteTerminal.kind === 'consumed'
              ? 'text-blue-800'
              : 'text-amber-800';
          return (
          <div className={`mb-6 p-5 rounded-2xl flex flex-col gap-4 ${bannerTone}`}>
            <div className="flex items-start gap-4">
              <AlertCircle className={`w-6 h-6 mt-0.5 flex-shrink-0 ${iconTone}`} />
              <div className={`text-base font-inter leading-relaxed ${textTone}`}>
                <p>{inviteTerminal.message}</p>
              </div>
            </div>
            {shouldShowInviteSignIn(inviteTerminal) && (
              <div className="flex flex-col sm:flex-row gap-3 pl-10">
                <button
                  type="button"
                  onClick={() => goToLogin(inviteTerminal.email)}
                  className="py-3 px-6 bg-[color:var(--pmc-accent)] text-white rounded-full hover:bg-[color:var(--pmc-accent-hover)] transition-colors font-poppins font-medium"
                >
                  Sign in
                </button>
                <Link
                  to="/forgot-password"
                  className="py-3 px-6 text-center text-[color:var(--pmc-accent)] hover:underline font-inter font-medium"
                >
                  Forgot password?
                </Link>
              </div>
            )}
          </div>
          );
        })()}

        {error && (
          <div className="mb-6 p-6 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-base text-red-700 font-inter">{error}</p>
          </div>
        )}

          {!shouldHideSignupForm(inviteTerminal) && (
          <form onSubmit={handleSignup} className="space-y-6">
          {/* Name Input */}
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2 font-inter">
              Full Name
            </label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              {/* autoComplete="name" marks this as a person's name, NOT the
                  login identifier. Without it, password managers heuristically
                  store the name as the saved "username" (PAW-127). */}
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[color:var(--pmc-accent)] transition-colors font-inter text-base"
                placeholder="Enter your full name"
                required
                autoComplete="name"
              />
            </div>
          </div>

          {/* Email Input */}
          {/*
              When arriving via a magic-link invite the email is bound to
              the invitation. We render the prefilled email as a plain
              <div> instead of an <input> so:
                (a) Chrome / 1Password / Bitwarden can't autofill over it
                    (this was happening with readOnly inputs — autofill
                    ignores readOnly),
                (b) the value is never sent from this DOM node — the
                    submit handler reads `invite.prefill.email` directly,
                (c) it's visually obvious the field is not interactive.
              We keep `name` editable since PMC-recorded names are often
              imperfect ("J. Doe", all-caps, etc.).
          */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 font-inter">
              {invite && inviteAlternateEmail(invite.prefill)
                ? 'Primary email (lease identity)'
                : 'Email Address'}
              {invite && (
                <span className="ml-2 text-xs font-normal text-gray-500">
                  {inviteAlternateEmail(invite.prefill)
                    ? '(locked — your account registers under this address)'
                    : '(locked — invitation was sent to this address)'}
                </span>
              )}
            </label>
            {invite ? (
              <div className="space-y-4">
                {/* Hidden username anchor. The visible email above is a
                    non-interactive <div> so autofill can't clobber it, but
                    password managers only read <input> controls — with no
                    input they save the Full Name as the credential username
                    (PAW-127). This visually-hidden, read-only input gives them
                    the address the account registers/signs in under
                    (invite.prefill.email — same value handleSignup submits).
                    It is never read on submit and carries tabIndex/aria-hidden
                    so it stays out of keyboard and screen-reader flow. */}
                <input
                  type="email"
                  name="username"
                  autoComplete="username"
                  value={invite.prefill.email || ''}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                  className="sr-only"
                />
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <div
                    className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 bg-gray-50 text-gray-700 rounded-xl font-inter text-base select-all"
                    aria-label="Primary invitation email (locked)"
                  >
                    {invitePrimaryEmail(invite.prefill) || invite.prefill.email}
                  </div>
                </div>
                {inviteAlternateEmail(invite.prefill) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 font-inter">
                      Deliverable email
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        (invitation sent here — sign in with this after registration)
                      </span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <div
                        className="w-full pl-12 pr-4 py-4 border-2 border-blue-100 bg-blue-50 text-gray-800 rounded-xl font-inter text-base select-all"
                        aria-label="Deliverable invitation email"
                      >
                        {inviteAlternateEmail(invite.prefill)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[color:var(--pmc-accent)] transition-colors font-inter text-base"
                  placeholder="Enter your email"
                  required
                  autoComplete="username"
                />
              </div>
            )}
          </div>

          {/* Password Input */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2 font-inter">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full pl-12 pr-12 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[color:var(--pmc-accent)] transition-colors font-inter text-base"
                placeholder="Create a password (min. 8 characters)"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Confirm Password Input */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2 font-inter">
              Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="w-full pl-12 pr-12 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[color:var(--pmc-accent)] transition-colors font-inter text-base"
                placeholder="Confirm your password"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-5 px-8 bg-[color:var(--pmc-accent)] text-white rounded-full hover:bg-[color:var(--pmc-accent-hover)] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-poppins text-lg font-medium"
          >
            {loading ? (
              <>
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Creating Account...</span>
              </>
            ) : (
              <span>Create Account</span>
            )}
          </button>
        </form>
          )}

            {/* Sign In Link */}
            {!inviteTerminal && (
            <div className="mt-8 text-center">
              <p className="text-base text-gray-600 font-inter">
                Already have an account?{' '}
                <Link to="/login" className="text-[color:var(--pmc-accent)] hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </div>
            )}

            {!inviteTerminal && (
            <div className="mt-6 text-center">
              <p className="text-base text-gray-500 font-inter leading-relaxed">
                By creating an account, you agree to our{' '}
                <a href="#" className="text-[color:var(--pmc-accent)] hover:underline font-medium">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-[color:var(--pmc-accent)] hover:underline font-medium">Privacy Policy</a>
              </p>
            </div>
            )}
      </div>
      
      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
        duration={8000}
      />
    </div>
  );
};

export default SignupPage;

